import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, parseNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Fuel, Loader2, CreditCard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FUEL_TYPES = ["Benzina", "Diesel", "GPL", "Metano", "Elettrico", "AdBlue"];

export default function NewRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isApprover = ["admin", "approvatore"].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(user?.id || "");
  const [myCards, setMyCards] = useState([]);
  const hasCard = myCards.length > 0;
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cardId, setCardId] = useState("");
  const [form, setForm] = useState({
    vehicle_id: "",
    usage_type: "km",
    usage_value: "",
    fuel_type: "Diesel",
    amount: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    if (isApprover) {
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    } else {
      api.get("/me/fuel-cards").then((r) => { setMyCards(r.data); if (r.data.length) setCardId(r.data[0].id); }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!isApprover) return;
    const emp = employees.find((e) => e.id === employeeId);
    const cards = emp?.fuel_cards || [];
    setMyCards(cards);
    setCardId(cards[0]?.id || "");
  }, [employeeId, employees]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) {
      toast.error("Seleziona un mezzo");
      return;
    }
    if (!hasCard) {
      toast.error(isApprover ? "L'utilizzatore selezionato non ha una carta carburante." : "Nessuna carta carburante assegnata. Contatta l'amministratore.");
      return;
    }
    if (!cardId) {
      toast.error("Seleziona la carta carburante");
      return;
    }
    setLoading(true);
    try {
      await api.post("/requests", {
        ...form,
        fuel_card_id: cardId,
        employee_id: isApprover ? employeeId : undefined,
        usage_value: parseNum(form.usage_value),
        amount: parseNum(form.amount),
      });
      toast.success(isApprover && employeeId !== user?.id ? "Richiesta creata a nome dell'utilizzatore" : "Richiesta inviata, in attesa di autorizzazione");
      navigate(isApprover ? "/richieste" : "/le-mie-richieste");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">
          Nuova richiesta di rifornimento
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Compila i dati del rifornimento da autorizzare.
        </p>
      </div>

      {isApprover && (
        <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50/50 p-4">
          <Label className="text-xs font-medium uppercase tracking-wide text-slate-500">Utilizzatore (a nome di)</Label>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger data-testid="request-employee-select" className="mt-1.5 max-w-md bg-white"><SelectValue placeholder="Seleziona l'utilizzatore" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id} data-testid={`request-employee-option-${e.id}`}>{e.name}{e.id === user?.id ? " (io)" : ""}{e.fuel_cards?.length ? "" : " — senza carta"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-slate-400">Come approvatore puoi creare una richiesta a nome di un altro utilizzatore.</p>
        </div>
      )}

      <div className={`mb-6 flex items-center gap-3 rounded-lg border p-4 ${hasCard ? "border-slate-200 bg-white" : "border-rose-200 bg-rose-50"}`}>
        <div className={`flex h-10 w-10 items-center justify-center rounded-md ${hasCard ? "bg-blue-50" : "bg-rose-100"}`}>
          {hasCard ? (
            <CreditCard className="h-5 w-5 text-[#002FA7]" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-rose-600" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Carta carburante
          </p>
          {hasCard ? (
            <div className="mt-1.5">
              <Select value={cardId} onValueChange={setCardId}>
                <SelectTrigger data-testid="request-card-select" className="max-w-md"><SelectValue placeholder="Seleziona la carta" /></SelectTrigger>
                <SelectContent>
                  {myCards.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`request-card-option-${c.id}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {myCards.length > 1 && <p className="mt-1 text-xs text-slate-400">Hai {myCards.length} carte associate: seleziona quella usata per questo rifornimento.</p>}
            </div>
          ) : (
            <p className="mt-1 text-sm text-rose-600" data-testid="no-card-warning">
              Non puoi inviare richieste finché l'amministratore non ti assegna una carta carburante.
            </p>
          )}
        </div>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading">
            <Fuel className="h-5 w-5 text-[#002FA7]" /> Dettagli
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label>Mezzo</Label>
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => { const veh = vehicles.find((x) => x.id === v); setForm({ ...form, vehicle_id: v, usage_type: veh?.consumption_type === "ore" ? "ore" : "km" }); }}
              >
                <SelectTrigger className="mt-1.5" data-testid="vehicle-select">
                  <SelectValue placeholder="Seleziona un mezzo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.length === 0 && (
                    <div className="px-2 py-3 text-sm text-slate-400">
                      Nessun mezzo disponibile
                    </div>
                  )}
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`vehicle-option-${v.id}`}>
                      {v.name} {v.plate ? `(${v.plate})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tipo di indicazione</Label>
              <div
                data-testid="usage-type-auto"
                className="mt-2 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm"
              >
                {!form.vehicle_id ? (
                  <span className="text-slate-400">Seleziona prima un mezzo</span>
                ) : (
                  <>
                    <span className="font-medium text-slate-800">{form.usage_type === "km" ? "Km percorsi" : "Ore di utilizzo"}</span>
                    <span className="text-xs text-slate-400">· rilevato dal tipo di consumo del mezzo</span>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="usage_value">
                  {form.usage_type === "km" ? "Km percorsi" : "Ore di utilizzo"}
                </Label>
                <Input
                  id="usage_value"
                  type="text"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  data-testid="usage-value-input"
                  value={form.usage_value}
                  onChange={(e) => setForm({ ...form, usage_value: e.target.value })}
                  required
                  className="mt-1.5"
                  placeholder={form.usage_type === "km" ? "es. 320" : "es. 8"}
                />
              </div>
              <div>
                <Label htmlFor="amount">Importo da autorizzare (€)</Label>
                <Input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  data-testid="amount-input"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  className="mt-1.5"
                  placeholder="es. 75.00"
                />
              </div>
            </div>

            <div>
              <Label>Tipo di carburante</Label>
              <Select
                value={form.fuel_type}
                onValueChange={(v) => setForm({ ...form, fuel_type: v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="fuel-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FUEL_TYPES.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">Note (facoltative)</Label>
              <Textarea
                id="notes"
                data-testid="notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5"
                placeholder="Eventuali dettagli aggiuntivi"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !hasCard}
              data-testid="submit-refuel-request-btn"
              className="w-full rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Invia richiesta
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
